import React, { ReactNode, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useDebounce } from '../../../hooks/useDebounce'

import Icon from '../../ui/Icon'
import { SearchInput } from '../../input/searchInput/SearchInput'

import {
  DisplayType,
  SliceResponse,
  SortDirectionType,
  SortType,
  ViewMode,
} from '../../../types/listing'

type ListingProps<T> = {
  response: SliceResponse<T>
  renderItem: (item: T, viewMode: ViewMode) => React.ReactNode
  onLoadMore: () => void
  loading: boolean
  emptyMessage?: string
  onSearchChange?: (value: string) => void
  displaySearch?: boolean
  searchPlaceholder?: string
  sortOptions?: {
    label: string
    value: string
  }[]
  currentSortField?: SortType
  currentSortDirection?: SortDirectionType
  onSortChange?: (field: SortType, direction: SortDirectionType) => void
  tableColumns?: {
    key: string
    label: string
    align?: string
  }[]
  title?: string
  filter?: ReactNode
  initialViewMode?: ViewMode
  gridItemClassName?: string
  mobileGridItemClassName?: string
}

export const Listing = <T,>({
  response,
  renderItem,
  onLoadMore,
  loading,
  emptyMessage,
  onSearchChange,
  displaySearch,
  searchPlaceholder,
  sortOptions,
  tableColumns,
  title,
  filter,
  initialViewMode = DisplayType.LIST,
  gridItemClassName = 'col-12 col-md-6 col-xl-4',
  mobileGridItemClassName = 'col-12 col-xl-6',
}: ListingProps<T>) => {
  const { t } = useTranslation('common')

  const [searchInput, setSearchInput] = useState('')

  const debouncedSearch = useDebounce(searchInput, 300)

  const loadMoreRef = useRef<HTMLDivElement>(null)
  const loadingRef = useRef(false)
  const requestedPageRef = useRef(-1)

  const { content, pageable } = response

  const displayHeader = Boolean(filter || displaySearch || sortOptions?.length)

  const getScrollParent = (element: HTMLElement | null): Element | null => {
    if (!element) {
      return null
    }

    let parent = element.parentElement

    while (parent) {
      const { overflowY } = window.getComputedStyle(parent)

      if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') {
        return parent
      }

      parent = parent.parentElement
    }

    return null
  }

  useEffect(() => {
    onSearchChange?.(debouncedSearch)
  }, [debouncedSearch, onSearchChange])

  useEffect(() => {
    loadingRef.current = loading
  }, [loading])

  useEffect(() => {
    requestedPageRef.current = pageable.pageNumber
  }, [pageable.pageNumber])

  useEffect(() => {
    const sentinel = loadMoreRef.current
    const scrollRoot = getScrollParent(sentinel)

    if (!sentinel || pageable.last) {
      return
    }

    const observer = new IntersectionObserver(
      entries => {
        const [entry] = entries

        if (!entry?.isIntersecting || loadingRef.current) {
          return
        }

        if (requestedPageRef.current !== pageable.pageNumber) {
          return
        }

        requestedPageRef.current += 1
        loadingRef.current = true
        onLoadMore()
      },
      {
        root: scrollRoot,
        rootMargin: '120px',
        threshold: 0,
      }
    )

    observer.observe(sentinel)

    return () => {
      observer.disconnect()
    }
  }, [pageable.pageNumber, pageable.last, onLoadMore])

  const renderGrid = (mobile = false) => {
    const itemClassName = mobile ? mobileGridItemClassName : gridItemClassName
    const singleItemClassName =
      content.length === 1 && pageable.last ? 'listing-single-item' : ''

    return (
      <div className="row g-3">
        {content.map((item, index) => (
          <div
            key={index}
            className={`${itemClassName} ${singleItemClassName}`}
          >
            {renderItem(item, DisplayType.GRID)}
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="listing">
      {displayHeader && (
        <div className="row listing-header">
          {filter && (
            <div
              className={`col-12 ${
                sortOptions?.length ? 'col-xxl-8' : 'col-xxl-9'
              } listing-filter`}
            >
              {filter}
            </div>
          )}

          {displaySearch && (
            <div className="col-12 col-xxl-3 listing-search">
              <SearchInput
                iconName="search"
                value={searchInput}
                onChange={event => setSearchInput(event.target.value)}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
              />
            </div>
          )}

          {Boolean(sortOptions?.length) && (
            <div className="col-12 col-xxl-1 listing-sort-button">
              <button type="button" className="btn btn-ghost">
                <Icon name="filter" size={14} />

                <span>{t('filter')}</span>
              </button>
            </div>
          )}
        </div>
      )}

      {content.length === 0 ? (
        <p>{emptyMessage ?? t('empty-list')}</p>
      ) : (
        <>
          {title && <h3 className="mb-3">{title}</h3>}

          {initialViewMode === DisplayType.LIST ? (
            <>
              <div className="ph-table-wrap listing-table-wrapper">
                <table className="ph-table listing-table">
                  {tableColumns && (
                    <thead>
                      <tr>
                        {tableColumns.map(column =>
                          column.key === 'actions' ? (
                            <th key={column.key} className="listing-actions-col">
                              <span className="visually-hidden">{column.label}</span>
                            </th>
                          ) : (
                            <th
                              key={column.key}
                              style={{
                                textAlign:
                                  column.align === 'end'
                                    ? 'right'
                                    : column.align === 'center'
                                      ? 'center'
                                      : 'left',
                              }}
                            >
                              {column.label}
                            </th>
                          )
                        )}
                      </tr>
                    </thead>
                  )}

                  <tbody>
                    {content.map((item, index) => (
                      <React.Fragment key={index}>
                        {renderItem(item, DisplayType.LIST)}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="listing-mobile">{renderGrid(true)}</div>
            </>
          ) : (
            renderGrid()
          )}

          {!pageable.last && (
            <div
              ref={loadMoreRef}
              className="listing-load-more-sentinel"
              aria-hidden="true"
            />
          )}

          {loading && <div className="listing-loading">{t('loading')}</div>}
        </>
      )}
    </div>
  )
}
